import { useCallback, useEffect, useState } from "react";
import { useJobStore } from "./stores/jobStore";
import { useAuthStore } from "./stores/authStore";
import { JobsTable } from "./components/dashboard/JobsTable";
import { JobDetail } from "./components/dashboard/JobDetail";
import { UploadZone } from "./components/dashboard/UploadZone";
import { HITLFrame } from "./components/hitl/HITLFrame";
import { LoginPage } from "./components/auth/LoginPage";
import { ModelManager } from "./components/settings/ModelManager";
import { AITraining } from "./components/settings/AITraining";
import { PanelCrop } from "./components/settings/PanelCrop";
import { MainView } from "./components/main/MainView";

type View = "main" | "dashboard" | "models" | "training" | "batch-crop";


function Logo() {
  return (
    <div className="flex items-center gap-2.5">
      <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-blue-500 to-blue-700 flex items-center justify-center shadow-glow-blue flex-shrink-0">
        <span className="text-xs font-bold text-white">R</span>
      </div>
      <div>
        <p className="text-sm font-semibold text-white leading-none">RapidDigitizer</p>
        <p className="text-[10px] text-slate-500 leading-none mt-0.5">AI Pipeline</p>
      </div>
    </div>
  );
}

function StatChip({ label, value, color }: { label: string; value: number; color: string }) {
  if (value === 0) return null;
  return (
    <div className={`flex items-center gap-1 px-2 py-1 rounded-lg bg-rdp-card border border-rdp-border text-xs ${color}`}>
      <span className="font-bold font-mono">{value}</span>
      <span className="text-slate-500">{label}</span>
    </div>
  );
}

export default function App() {
  const { token, username, logout } = useAuthStore();
  const { loadJobs, jobs, selectedJob, selectedChart, hitlInit, hitlLoading, clearHITL } = useJobStore();
  const [showUpload, setShowUpload] = useState(false);
  const [view, setView] = useState<View>("batch-crop");

  if (!token) return <LoginPage />;

  useEffect(() => {
    loadJobs();
    const id = setInterval(loadJobs, 15_000);
    return () => clearInterval(id);
  }, [loadJobs]);

  const showHITL = ["NEEDS_REVIEW", "COMPLETED", "AUTO_APPROVED"].includes(selectedChart?.status ?? "") && hitlInit != null;

  const running     = jobs.filter((j) => j.status === "RUNNING").length;
  const hitlPending = jobs.reduce((s, j) => s + j.pending_hitl_count, 0);
  const completed   = jobs.filter((j) => j.status === "COMPLETED").length;

  const handleUploadDone = useCallback(() => setShowUpload(false), []);

  return (
    <div className="flex h-screen bg-rdp-bg overflow-hidden">

      {/* ── Sidebar ────────────────────────────────────────────────────── */}
      <aside className="w-72 flex-shrink-0 flex flex-col border-r border-rdp-border">

        {/* Header */}
        <div className="px-4 py-4 border-b border-rdp-border flex items-center justify-between gap-3">
          <Logo />
          <button
            onClick={() => setShowUpload(true)}
            className="btn-primary flex items-center gap-1.5 text-xs py-1.5 px-3"
          >
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 4v16m8-8H4" />
            </svg>
            New Job
          </button>
        </div>

        {/* Nav tabs */}
        <div className="px-3 py-2 border-b border-rdp-border flex gap-1 flex-wrap">
          {(["main", "batch-crop", "models", "training"] as View[]).map((v) => (
            <button
              key={v}
              onClick={() => setView(v)}
              className={[
                "flex-1 py-1.5 rounded-lg text-xs font-medium transition-colors cursor-pointer capitalize",
                view === v ? "bg-rdp-hover text-white" : "text-slate-500 hover:text-slate-300",
              ].join(" ")}
            >
              {v === "batch-crop" ? "Batch Crop" : v === "models" ? "AI Models" : v.charAt(0).toUpperCase() + v.slice(1)}
            </button>
          ))}
        </div>

        {/* Stats (dashboard only) */}
        {view === "dashboard" && jobs.length > 0 && (
          <div className="px-3 py-2 flex items-center gap-1.5 border-b border-rdp-border flex-wrap">
            <StatChip label="running"  value={running}     color="text-blue-400" />
            <StatChip label="HITL"     value={hitlPending} color="text-amber-400" />
            <StatChip label="done"     value={completed}   color="text-emerald-400" />
            {!running && !hitlPending && (
              <span className="text-[10px] text-slate-600 px-1">{jobs.length} total</span>
            )}
          </div>
        )}

        {/* Job list (dashboard only) */}
        <div className="flex-1 overflow-y-auto">
          {view === "dashboard" && <JobsTable />}
        </div>

        {/* Footer */}
        <div className="px-4 py-2.5 border-t border-rdp-border flex items-center justify-between gap-2">
          <span className="text-[10px] text-slate-600 font-mono truncate">{username}</span>
          <div className="flex items-center gap-2 flex-shrink-0">
            <button
              onClick={() => loadJobs()}
              className="text-[10px] text-slate-600 hover:text-slate-300 transition-colors cursor-pointer"
            >
              ↺
            </button>
            <button
              onClick={logout}
              className="text-[10px] text-slate-600 hover:text-red-400 transition-colors cursor-pointer"
            >
              sign out
            </button>
          </div>
        </div>
      </aside>

      {/* ── Main ───────────────────────────────────────────────────────── */}
      <main className="flex-1 overflow-hidden flex flex-col">
        {view === "main" ? (
          <MainView onOpenBatchCrop={() => setView("batch-crop")} />
        ) : view === "batch-crop" ? (
          <PanelCrop />
        ) : view === "models" ? (
          <ModelManager />
        ) : view === "training" ? (
          <AITraining />
        ) : showUpload ? (
          <div className="flex flex-col h-full">
            <div className="flex items-center justify-between px-5 py-3 border-b border-rdp-border bg-rdp-card/30">
              <h3 className="text-sm font-medium text-white">Upload New PDF</h3>
              <button onClick={handleUploadDone} className="btn-ghost text-slate-500">✕</button>
            </div>
            <div className="flex-1">
              <UploadZone />
            </div>
          </div>
        ) : selectedJob ? (
          <JobDetail />
        ) : (
          <UploadZone />
        )}
      </main>

      {/* ── HITL loading overlay ───────────────────────────────────────── */}
      {hitlLoading && !showHITL && (
        <div className="fixed inset-0 z-40 bg-rdp-bg/80 backdrop-blur-sm flex items-center justify-center">
          <div className="card px-8 py-6 flex flex-col items-center gap-4">
            <div className="w-10 h-10 rounded-full border-2 border-rdp-border border-t-blue-500 animate-spin" />
            <p className="text-slate-300 text-sm">Opening HITL editor…</p>
          </div>
        </div>
      )}

      {/* ── HITL full-screen ───────────────────────────────────────────── */}
      {showHITL && selectedChart && hitlInit && (
        <HITLFrame
          jobId={String(selectedChart.job_id)}
          init={hitlInit}
          onClose={clearHITL}
        />
      )}
    </div>
  );
}
